import { environment } from 'projects/semeris/src/environments/environment';

// Global functions replaced based on environment
export let measureFunction: typeof measureFunctionReal;
export let measureMethod: typeof measureMethodReal;

if (environment.envId === 'dev') {
  measureFunction = measureFunctionReal;
  measureMethod = measureMethodReal;
} else {
  measureFunction = measureFunctionNoop;
  measureMethod = measureMethodNoop;
}

// Global stats map
interface ExecutionStats {
  count: number;
  totalTime: number;
  maxTime: number;
  average?: number;
}
const executionStatsMap = new Map<string, ExecutionStats>();

// Table print
export function printExecutionStatsTable(includes?: string) {
  const map = includes
    ? new Map([...executionStatsMap].filter(([k]) => k.includes(includes)))
    : executionStatsMap;

  const data = Object.fromEntries([...map].sort(([, a], [, b]) => b.totalTime - a.totalTime));
  Object.values(data).forEach((stat) => (stat.average = stat.totalTime / stat.count));

  console.table(data);
}

export function resetExecutionStatsMap() {
  executionStatsMap.clear();
}

/*
  Function to measure function execution time
  
  example:
    function exampleMeasuredFunction(p1: string, p2: string): string {
      return measureAs('exampleMeasuredFunction', () => {
        console.log('measureAs: This function is measured');
        return p1 + p2;
      });
    }
*/
function measureFunctionReal<T>(key: string, fn: () => T): T {
  const before = performance.now();
  try {
    return fn();
  } finally {
    const elapsedTime = performance.now() - before;
    const stats = executionStatsMap.get(key) || { count: 0, totalTime: 0, maxTime: 0 };
    stats.count += 1;
    stats.totalTime += elapsedTime;
    if (elapsedTime > stats.maxTime) {
      stats.maxTime = elapsedTime;
    }
    executionStatsMap.set(key, stats); // Set stats back to the map
  }
}

/*
  decorator to measure method execution time

  example:
    class Example {
            exampleMethod(p1: string, p2: string): string {
        console.log('measureMethod: This method is measured');
        return p1 + p2;
      }
    }
*/
function measureMethodReal(key: string) {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const originalMethod = descriptor.value;
    descriptor.value = function (...args: any[]) {
      const before = performance.now();

      let result;
      try {
        result = originalMethod.apply(this, args); // Apply method logic
      } finally {
        const elapsedTime = performance.now() - before;
        const stats = executionStatsMap.get(key) || { count: 0, totalTime: 0, maxTime: 0 };
        stats.count += 1;
        stats.totalTime += elapsedTime;
        if (elapsedTime > stats.maxTime) {
          stats.maxTime = elapsedTime;
        }
        executionStatsMap.set(key, stats); // Set stats back to the map
      }

      return result;
    };

    return descriptor;
  };
}

function measureClassReal(constructor: Function) {
  const className = constructor.name;
  const propertyNames = Object.getOwnPropertyNames(constructor.prototype);

  console.log(`Measuring class: ${className}`);

  for (const propertyName of propertyNames) {
    // Skip the constructor
    if (propertyName === 'constructor') {
      continue;
    }

    // Get the property descriptor
    const descriptor = Object.getOwnPropertyDescriptor(constructor.prototype, propertyName);
    if (!descriptor) {
      continue;
    }

    // Handle methods
    if (typeof descriptor.value === 'function') {
      const methodKey = `${className}.${propertyName}`;
      descriptor.value = wrapMeasurement(descriptor.value, methodKey);
      Object.defineProperty(constructor.prototype, propertyName, descriptor);
    }
    // Handle getters and setters
    else {
      const { get: originalGet, set: originalSet } = descriptor;

      if (originalGet) {
        const methodKey = `${className}.${propertyName} (get)`;
        descriptor.get = wrapMeasurement(originalGet, methodKey);
      }

      if (originalSet) {
        const methodKey = `${className}.${propertyName} (set)`;
        descriptor.set = wrapMeasurement(originalSet, methodKey);
      }

      Object.defineProperty(constructor.prototype, propertyName, descriptor);
    }
  }
}

// Helper function to wrap any function (method, getter, setter)
function wrapMeasurement(originalFunction: Function, methodKey: string) {
  return function (...args: any[]) {
    const startTime = performance.now();
    let result;
    try {
      result = originalFunction.apply(this, args);
    } finally {
      const elapsedTime = performance.now() - startTime;
      const stats = executionStatsMap.get(methodKey) || { count: 0, totalTime: 0, maxTime: 0 };
      stats.count += 1;
      stats.totalTime += elapsedTime;
      stats.maxTime = Math.max(stats.maxTime, elapsedTime);
      executionStatsMap.set(methodKey, stats);
    }
    return result;
  };
}

// noop for production
function measureFunctionNoop<T>(key: string, fn: () => T): T {
  return fn();
}
function measureMethodNoop(key: string) {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    return descriptor;
  };
}
